import React, { useState, useEffect, useMemo } from 'react';
import { Container, Table, Button, Badge, Alert, Spinner, Modal, Form, Row, Col, InputGroup, Card } from 'react-bootstrap';
import api from '../../api/axios';
import { useLanguage } from '../../contexts/LanguageContext';

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [showAnonymizeModal, setShowAnonymizeModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [expandedUserIds, setExpandedUserIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [bulkAction, setBulkAction] = useState('');
  const [bulkReason, setBulkReason] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [resetLinkLoading, setResetLinkLoading] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editUserId, setEditUserId] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'customer',
    company_id: '',
    order_confirmation_emails: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: ''
  });
  const [createForm, setCreateForm] = useState({
    username: '',
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    role: 'customer',
    company_id: '',
    order_confirmation_emails: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    phone: ''
  });
  const { t } = useLanguage();

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
  }, []);

  useEffect(() => {
    setSelectedUserIds((prev) => prev.filter((id) => users.some((user) => user.id === id)));
  }, [users]);

  useEffect(() => {
    setExpandedUserIds((prev) => prev.filter((id) => users.some((user) => user.id === id)));
  }, [users]);

  const filteredUsers = useMemo(() => {
    const value = searchTerm.trim().toLowerCase();
    if (!value) {
      return users;
    }

    return users.filter((user) => {
      const haystack = [
        user.username,
        user.first_name,
        user.last_name,
        user.email,
        user.company,
        user.role,
        user.order_confirmation_emails,
        user.is_blocked ? 'blocked' : 'active'
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(value);
    });
  }, [users, searchTerm]);

  const allFilteredSelected = filteredUsers.length > 0 && filteredUsers.every((user) => selectedUserIds.includes(user.id));

  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/admin/users');
      setUsers(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Fetch users error:', err);
      setError('Failed to load users');
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/api/admin/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Fetch companies error:', err);
      setError('Failed to load companies');
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      username: '',
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      role: 'customer',
      company_id: '',
      order_confirmation_emails: '',
      address_line1: '',
      address_line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      phone: ''
    });
  };

  const handleCreateFormChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    setCreateLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post('/api/admin/users', {
        username: createForm.username,
        first_name: createForm.first_name || null,
        last_name: createForm.last_name || null,
        email: createForm.email,
        password: createForm.password,
        role: createForm.role,
        company_id: createForm.company_id || null,
        order_confirmation_emails: createForm.order_confirmation_emails,
        address: {
          address_line1: createForm.address_line1,
          address_line2: createForm.address_line2,
          city: createForm.city,
          state: createForm.state,
          postal_code: createForm.postal_code,
          country: createForm.country,
          phone: createForm.phone
        }
      });

      setSuccess('User created successfully');
      setShowCreateModal(false);
      resetCreateForm();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Create user error:', err);
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleExportUsers = async () => {
    try {
      const response = await api.get('/api/admin/users/export', {
        responseType: 'blob'
      });
      downloadBlob(new Blob([response.data]), 'users-export.xlsx');
      setSuccess('Users exported successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Export users error:', err);
      setError(err.response?.data?.error || 'Failed to export users');
    }
  };

  const handleDownloadUserTemplate = async () => {
    try {
      const response = await api.get('/api/admin/users/import-template', {
        responseType: 'blob'
      });
      downloadBlob(new Blob([response.data]), 'user-import-template.xlsx');
      setSuccess('Template downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Download user template error:', err);
      setError(err.response?.data?.error || 'Failed to download template');
    }
  };

  const handleImportUsers = async () => {
    if (!importFile) {
      setError('Please select a file to import');
      return;
    }

    setImportLoading(true);
    setError('');
    setSuccess('');

    try {
      const body = new FormData();
      body.append('file', importFile);

      const response = await api.post('/api/admin/users/import', body, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResults(response.data);
      setImportFile(null);
      setSuccess('User import completed');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Import users error:', err);
      setError(err.response?.data?.error || 'Failed to import users');
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenEditModal = (user) => {
    setEditUserId(user.id);
    setEditForm({
      username: user.username || '',
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      password: '',
      role: user.role || 'customer',
      company_id: user.company_id ? String(user.company_id) : '',
      order_confirmation_emails: user.order_confirmation_emails || '',
      address_line1: user.address_line1 || '',
      address_line2: user.address_line2 || '',
      city: user.city || '',
      state: user.state || '',
      postal_code: user.postal_code || '',
      country: user.country || '',
      phone: user.phone || ''
    });
    setShowEditModal(true);
  };

  const handleEditFormChange = (event) => {
    const { name, value } = event.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditUser = async (event) => {
    event.preventDefault();
    setEditLoading(true);
    setError('');
    setSuccess('');
    try {
      await api.put(`/api/admin/users/${editUserId}`, {
        username: editForm.username,
        first_name: editForm.first_name || null,
        last_name: editForm.last_name || null,
        email: editForm.email,
        password: editForm.password,
        role: editForm.role,
        company_id: editForm.company_id || null,
        order_confirmation_emails: editForm.order_confirmation_emails,
        address: {
          address_line1: editForm.address_line1,
          address_line2: editForm.address_line2,
          city: editForm.city,
          state: editForm.state,
          postal_code: editForm.postal_code,
          country: editForm.country,
          phone: editForm.phone
        }
      });
      setSuccess('User updated successfully');
      setShowEditModal(false);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Edit user error:', err);
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSendResetLink = async (user) => {
    setResetLinkLoading(user.id);
    setError('');
    setSuccess('');
    try {
      const response = await api.post(`/api/admin/users/${user.id}/send-reset-link`);
      setSuccess(response.data.message || `Password reset link sent to ${user.email}`);
      setTimeout(() => setSuccess(''), 6000);
    } catch (err) {
      console.error('Send reset link error:', err);
      setError(err.response?.data?.error || 'Failed to send password reset link');
    } finally {
      setResetLinkLoading(null);
    }
  };

  const handleShowBlockModal = (user) => {
    setSelectedUser(user);
    setBlockReason('');
    setShowBlockModal(true);
  };

  const handleCloseBlockModal = () => {
    setShowBlockModal(false);
    setSelectedUser(null);
    setBlockReason('');
  };

  const handleShowAnonymizeModal = (user) => {
    setSelectedUser(user);
    setShowAnonymizeModal(true);
  };

  const handleCloseAnonymizeModal = () => {
    setShowAnonymizeModal(false);
    setSelectedUser(null);
  };

  const handleBlockUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(
        `/api/admin/users/${selectedUser.id}/block`,
        { reason: blockReason }
      );

      setSuccess(`User "${selectedUser.username}" has been blocked`);
      handleCloseBlockModal();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Block user error:', err);
      setError(err.response?.data?.error || 'Failed to block user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnblockUser = async (user) => {
    if (!window.confirm(`Are you sure you want to unblock "${user.username}"?`)) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(
        `/api/admin/users/${user.id}/unblock`,
        {}
      );

      setSuccess(`User "${user.username}" has been unblocked`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Unblock user error:', err);
      setError(err.response?.data?.error || 'Failed to unblock user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${user.username}"? This action cannot be undone.`)) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.delete(`/api/admin/users/${user.id}`);

      setSuccess(`User "${user.username}" has been deleted`);
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Delete user error:', err);
      setError(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAnonymizeUser = async () => {
    if (!selectedUser) return;

    setActionLoading(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/api/admin/users/${selectedUser.id}/anonymize`, {});

      setSuccess(`User "${selectedUser.username}" has been anonymized`);
      handleCloseAnonymizeModal();
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Anonymize user error:', err);
      setError(err.response?.data?.error || 'Failed to anonymize user');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleUserExpanded = (userId) => {
    setExpandedUserIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ));
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) => (
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    ));
  };

  const toggleSelectAllFilteredUsers = () => {
    if (allFilteredSelected) {
      const filteredIds = new Set(filteredUsers.map((user) => user.id));
      setSelectedUserIds((prev) => prev.filter((id) => !filteredIds.has(id)));
      return;
    }

    setSelectedUserIds((prev) => Array.from(new Set([
      ...prev,
      ...filteredUsers.map((user) => user.id)
    ])));
  };

  const handleBulkUsers = async () => {
    if (selectedUserIds.length === 0) {
      setError('Select at least one user first');
      return;
    }

    if (!bulkAction) {
      setError('Select a bulk action first');
      return;
    }

    const message = bulkAction === 'block'
      ? `Are you sure you want to block ${selectedUserIds.length} selected users?`
      : `Are you sure you want to delete ${selectedUserIds.length} selected users? This action cannot be undone.`;

    if (!window.confirm(message)) {
      return;
    }

    setBulkLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post('/api/admin/users/bulk', {
        ids: selectedUserIds,
        action: bulkAction,
        reason: bulkAction === 'block' ? bulkReason : undefined
      });
      const result = response.data;
      const errorCount = result.errors?.length || 0;

      setSuccess(`Processed ${result.processed} users${errorCount > 0 ? `, ${errorCount} errors` : ''}`);
      setError(errorCount > 0
        ? result.errors.map((item) => `#${item.id}: ${item.error}`).join(' | ')
        : '');
      setSelectedUserIds([]);
      setBulkAction('');
      setBulkReason('');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Bulk user action error:', err);
      setError(err.response?.data?.error || 'Failed to process selected users');
    } finally {
      setBulkLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <Container className="loading-spinner">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <div className="d-flex justify-content-between align-items-center mb-4 gap-2 flex-wrap">
        <h1 className="mb-0">{t('admin.userManagement')}</h1>
        <div className="d-flex gap-2 flex-wrap">
          <Button variant="outline-success" onClick={handleExportUsers} title={t('admin.exportExcel')} aria-label={t('admin.exportExcel')} style={{fontSize:'1.75rem'}}>
            📊
          </Button>
          <Button variant="outline-primary" onClick={() => {
            setShowImportModal(true);
            setImportResults(null);
          }} title={t('admin.importExcel')} aria-label={t('admin.importExcel')} style={{fontSize:'1.75rem'}}>
            📥
          </Button>
          <Button variant="primary" onClick={() => setShowCreateModal(true)} title={t('admin.addUser')} aria-label={t('admin.addUser')} style={{fontSize:'1.75rem'}}>
            ⊕
          </Button>
        </div>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Label>{t('admin.filterUsers')}</Form.Label>
              <InputGroup>
                <InputGroup.Text>Search</InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={`${t('admin.filterUsers')}...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Label>{t('admin.bulkAction')}</Form.Label>
              <Form.Select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)} disabled={bulkLoading}>
                <option value="">{t('admin.selectAction')}</option>
                <option value="block">{t('admin.block')}</option>
                <option value="delete">{t('common.delete')}</option>
              </Form.Select>
            </Col>
            <Col md={2}>
              <Form.Label>Reason</Form.Label>
              <Form.Control
                type="text"
                placeholder="Optional"
                value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)}
                disabled={bulkLoading || bulkAction !== 'block'}
              />
            </Col>
            <Col md={2}>
              <div className="small text-muted mb-2">{t('admin.selected')} {selectedUserIds.length}</div>
              <Button className="w-100" onClick={handleBulkUsers} disabled={bulkLoading || selectedUserIds.length === 0}>
                {bulkLoading ? t('admin.applying') : t('admin.apply')}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th style={{ width: '44px' }}>
              <Form.Check
                type="checkbox"
                checked={allFilteredSelected}
                onChange={toggleSelectAllFilteredUsers}
                aria-label="Select all filtered users"
              />
            </th>
            <th>{t('admin.id')}</th>
            <th>{t('auth.username')}</th>
            <th>Name</th>
            <th>{t('auth.email')}</th>
            <th>{t('admin.orderConfirmationEmailsCol')}</th>
            <th>{t('admin.company')}</th>
            <th>{t('admin.role')}</th>
            <th>{t('admin.status')}</th>
            <th>{t('admin.registered')}</th>
            <th>{t('admin.actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredUsers.map((user) => {
            const isExpanded = expandedUserIds.includes(user.id);

            return (
              <React.Fragment key={user.id}>
                <tr className={user.is_blocked ? 'table-danger' : ''}>
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => toggleUserExpanded(user.id)}
                        aria-label={isExpanded ? 'Collapse user details' : 'Expand user details'}
                        title={isExpanded ? 'Collapse user details' : 'Expand user details'}
                      >
                        {isExpanded ? '▾' : '▸'}
                      </Button>
                      <Form.Check
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        aria-label={`Select user ${user.username}`}
                        disabled={bulkLoading}
                      />
                    </div>
                  </td>
                  <td>{user.id}</td>
                  <td>{user.username}</td>
                  <td>{[user.first_name, user.last_name].filter(Boolean).join(' ') || '-'}</td>
                  <td>{user.email}</td>
                  <td style={{ minWidth: '260px' }}>
                    <div>{user.order_confirmation_emails || '-'}</div>
                  </td>
                  <td>
                    <div>{user.company || '-'}</div>
                  </td>
                  <td>
                    {user.role === 'admin' ? (
                      <Badge bg="danger">{t('admin.adminRole')}</Badge>
                    ) : user.role === 'manager' ? (
                      <Badge bg="primary">{t('admin.managerRole')}</Badge>
                    ) : (
                      <Badge bg="secondary">{t('admin.userRole')}</Badge>
                    )}
                  </td>
                  <td>
                    {user.is_blocked ? (
                      <div>
                        <Badge bg="danger" className="mb-1">{t('admin.blocked')}</Badge>
                        {user.blocked_reason && (
                          <div className="small text-muted">
                            Reason: {user.blocked_reason}
                          </div>
                        )}
                        {user.blocked_at && (
                          <div className="small text-muted">
                            Blocked: {formatDate(user.blocked_at)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <Badge bg="success">{t('admin.active')}</Badge>
                    )}
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="d-flex gap-2 flex-wrap">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleOpenEditModal(user)}
                        disabled={actionLoading}
                        title={t('common.edit')}
                        aria-label={t('common.edit')}
                      >
                        ✎
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => handleSendResetLink(user)}
                        disabled={actionLoading || resetLinkLoading === user.id}
                        title={`Send password reset link to ${user.email}`}
                        aria-label={t('admin.sendResetLink')}
                      >
                        {resetLinkLoading === user.id ? '…' : '🔐'}
                      </Button>
                      {!user.is_blocked ? (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={() => handleShowBlockModal(user)}
                          disabled={actionLoading}
                          title={t('admin.block')}
                          aria-label={t('admin.block')}
                        >
                          ⛔
                        </Button>
                      ) : (
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleUnblockUser(user)}
                          disabled={actionLoading}
                          title={t('admin.unblock')}
                          aria-label={t('admin.unblock')}
                        >
                          ✓
                        </Button>
                      )}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteUser(user)}
                        disabled={actionLoading || user.role === 'admin'}
                        title={t('common.delete')}
                        aria-label={t('common.delete')}
                      >
                        🗑
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleShowAnonymizeModal(user)}
                        disabled={actionLoading || user.role === 'admin'}
                        title={t('admin.anonymize')}
                        aria-label={t('admin.anonymize')}
                      >
                        👤
                      </Button>
                    </div>
                  </td>
                </tr>

                {isExpanded && (
                  <tr>
                    <td colSpan={11} className="bg-light">
                      <div className="p-2 small">
                        <strong>{t('auth.addressDetails')}:</strong>{' '}
                        {[
                          user.address_line1,
                          user.address_line2,
                          user.city,
                          user.state,
                          user.postal_code,
                          user.country
                        ].filter(Boolean).join(', ') || t('common.na')}
                        {user.phone && (
                          <div><strong>{t('auth.phone')}:</strong> {user.phone}</div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>

      {filteredUsers.length === 0 && (
        <Alert variant="info">{t('products.noResults')}</Alert>
      )}

      {users.length === 0 && (
        <Alert variant="info">No users found</Alert>
      )}

      {/* Block User Modal */}
      <Modal show={showBlockModal} onHide={handleCloseBlockModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.blockUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                You are about to block user: <strong>{selectedUser.username}</strong>
              </p>
              <p className="text-muted">
                Blocked users will not be able to log in or access their account.
              </p>
              
              <Form.Group className="mb-3">
                <Form.Label>Reason for blocking (optional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  placeholder="e.g., Violation of terms of service, suspicious activity, etc."
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseBlockModal} disabled={actionLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleBlockUser} disabled={actionLoading}>
            {actionLoading ? t('common.loading') : t('admin.block')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showCreateModal} onHide={() => {
        if (!createLoading) {
          setShowCreateModal(false);
          resetCreateForm();
        }
      }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.addUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleCreateUser}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.username')}</Form.Label>
                  <Form.Control name="username" value={createForm.username} onChange={handleCreateFormChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control name="first_name" value={createForm.first_name} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control name="last_name" value={createForm.last_name} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.email')}</Form.Label>
                  <Form.Control type="email" name="email" value={createForm.email} onChange={handleCreateFormChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.password')}</Form.Label>
                  <Form.Control type="password" name="password" value={createForm.password} onChange={handleCreateFormChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t('admin.role')}</Form.Label>
                  <Form.Select name="role" value={createForm.role} onChange={handleCreateFormChange}>
                    <option value="customer">{t('admin.userRole')}</option>
                    <option value="manager">{t('admin.managerRole')}</option>
                    <option value="admin">{t('admin.adminRole')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t('admin.company')}</Form.Label>
                  <Form.Select
                    name="company_id"
                    value={createForm.company_id}
                    onChange={handleCreateFormChange}
                  >
                    <option value="">-</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('admin.orderConfirmationEmailsCol')}</Form.Label>
                  <Form.Control
                    name="order_confirmation_emails"
                    value={createForm.order_confirmation_emails}
                    onChange={handleCreateFormChange}
                    placeholder="orders@example.com, second@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={12}><h5 className="mb-0">{t('auth.addressDetails')}</h5></Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.address')}</Form.Label>
                  <Form.Control name="address_line1" value={createForm.address_line1} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.addressLine2Placeholder')}</Form.Label>
                  <Form.Control name="address_line2" value={createForm.address_line2} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.city')}</Form.Label>
                  <Form.Control name="city" value={createForm.city} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.stateProvince')}</Form.Label>
                  <Form.Control name="state" value={createForm.state} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.postalCode')}</Form.Label>
                  <Form.Control name="postal_code" value={createForm.postal_code} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.country')}</Form.Label>
                  <Form.Control name="country" value={createForm.country} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.phone')}</Form.Label>
                  <Form.Control name="phone" value={createForm.phone} onChange={handleCreateFormChange} />
                </Form.Group>
              </Col>
            </Row>
            <Modal.Footer className="px-0 pb-0">
              <Button variant="secondary" onClick={() => {
                setShowCreateModal(false);
                resetCreateForm();
              }} disabled={createLoading}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={createLoading}>
                {createLoading ? t('common.loading') : t('admin.addUser')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showEditModal} onHide={() => { if (!editLoading) setShowEditModal(false); }} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.editUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleEditUser}>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.username')}</Form.Label>
                  <Form.Control name="username" value={editForm.username} onChange={handleEditFormChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control name="first_name" value={editForm.first_name} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control name="last_name" value={editForm.last_name} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.email')}</Form.Label>
                  <Form.Control type="email" name="email" value={editForm.email} onChange={handleEditFormChange} required />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.password')} <span className="text-muted small">(leave blank to keep current)</span></Form.Label>
                  <Form.Control type="password" name="password" value={editForm.password} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t('admin.role')}</Form.Label>
                  <Form.Select name="role" value={editForm.role} onChange={handleEditFormChange}>
                    <option value="customer">{t('admin.userRole')}</option>
                    <option value="manager">{t('admin.managerRole')}</option>
                    <option value="admin">{t('admin.adminRole')}</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>{t('admin.company')}</Form.Label>
                  <Form.Select
                    name="company_id"
                    value={editForm.company_id}
                    onChange={handleEditFormChange}
                  >
                    <option value="">-</option>
                    {companies.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={12}>
                <Form.Group>
                  <Form.Label>{t('admin.orderConfirmationEmailsCol')}</Form.Label>
                  <Form.Control
                    name="order_confirmation_emails"
                    value={editForm.order_confirmation_emails}
                    onChange={handleEditFormChange}
                    placeholder="orders@example.com, second@example.com"
                  />
                </Form.Group>
              </Col>
              <Col md={12}><h5 className="mb-0">{t('auth.addressDetails')}</h5></Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.address')}</Form.Label>
                  <Form.Control name="address_line1" value={editForm.address_line1} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.addressLine2Placeholder')}</Form.Label>
                  <Form.Control name="address_line2" value={editForm.address_line2} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.city')}</Form.Label>
                  <Form.Control name="city" value={editForm.city} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.stateProvince')}</Form.Label>
                  <Form.Control name="state" value={editForm.state} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group>
                  <Form.Label>{t('auth.postalCode')}</Form.Label>
                  <Form.Control name="postal_code" value={editForm.postal_code} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.country')}</Form.Label>
                  <Form.Control name="country" value={editForm.country} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>{t('auth.phone')}</Form.Label>
                  <Form.Control name="phone" value={editForm.phone} onChange={handleEditFormChange} />
                </Form.Group>
              </Col>
            </Row>
            <Modal.Footer className="px-0 pb-0">
              <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={editLoading}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" variant="primary" disabled={editLoading}>
                {editLoading ? t('common.loading') : t('common.save')}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={showImportModal} onHide={() => !importLoading && setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.importUsers')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.selectExcelFile')}</Form.Label>
            <Form.Control
              type="file"
              accept=".xlsx,.xls"
              onChange={(event) => setImportFile(event.target.files?.[0] || null)}
              disabled={importLoading}
            />
          </Form.Group>
          <div className="d-flex gap-2 mb-3">
            <Button variant="outline-secondary" onClick={handleDownloadUserTemplate} disabled={importLoading} title={t('admin.downloadTemplate')} aria-label={t('admin.downloadTemplate')} style={{fontSize:'1.75rem'}}>
              📋
            </Button>
            <Button variant="primary" onClick={handleImportUsers} disabled={importLoading || !importFile}>
              {importLoading ? t('common.loading') : t('admin.importExcel')}
            </Button>
          </div>
          {importResults && (
            <Alert variant={importResults.errors?.length ? 'warning' : 'success'} className="mb-0">
              {t('admin.createdCount')}: {importResults.created || 0} | {t('admin.updatedCount')}: {importResults.updated || 0}
              {importResults.errors?.length > 0 && (
                <div className="mt-2 small">
                  {importResults.errors.map((item) => `Row ${item.row}: ${item.error}`).join(' | ')}
                </div>
              )}
            </Alert>
          )}
        </Modal.Body>
      </Modal>

      {/* Anonymize User Modal */}
      <Modal show={showAnonymizeModal} onHide={handleCloseAnonymizeModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.anonymizeUser')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>
                You are about to anonymize user: <strong>{selectedUser.username}</strong>
              </p>
              <p className="text-muted">
                This will remove personal data (username, email, company, addresses) and block the account.
              </p>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseAnonymizeModal} disabled={actionLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" onClick={handleAnonymizeUser} disabled={actionLoading}>
            {actionLoading ? t('common.loading') : t('admin.anonymize')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminUsers;
